import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Send, Wand2, Inbox, Mail, Phone, MessageCircle,
  User as UserIcon, ExternalLink, Archive, CheckCircle2, ArrowRight, X,
  Settings, PhoneMissed, Bot,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const STATUSES = ["new", "acknowledged", "assigned", "qualified",
                   "booked", "quoted", "won", "lost", "closed"];
const STATUS_TONE = {
  new: "bg-sky-50 text-sky-700 ring-sky-200",
  acknowledged: "bg-amber-50 text-amber-700 ring-amber-200",
  assigned: "bg-violet-50 text-violet-700 ring-violet-200",
  qualified: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  booked: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  quoted: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  won: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  lost: "bg-slate-100 text-slate-500 ring-slate-200",
  closed: "bg-slate-100 text-slate-500 ring-slate-200",
};
const CHAN_ICON = {
  form: <Inbox className="w-3.5 h-3.5" />,
  chat: <MessageCircle className="w-3.5 h-3.5" />,
  email: <Mail className="w-3.5 h-3.5" />,
  sms: <Phone className="w-3.5 h-3.5" />,
  whatsapp: <MessageCircle className="w-3.5 h-3.5" />,
  call: <Phone className="w-3.5 h-3.5" />,
};

export default function ConversationHubInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [aiMode, setAiMode] = useState("assist");

  useEffect(() => {
    axios.get(`${API}/hub/settings`, { headers: httpAuth() })
      .then((r) => setAiMode(r.data?.ai_mode || "assist"))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user === null) navigate("/login");
  }, [user, navigate]);

  const loadList = useCallback(async () => {
    try {
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`${API}/hub/conversations`,
        { params, headers: httpAuth() });
      setList(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch { setList([]); }
  }, [filter]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const r = await axios.get(`${API}/hub/conversations/${id}`,
        { headers: httpAuth() });
      setDetail(r.data);
    } catch { toast.error("Could not open conversation"); }
  }, []);

  useEffect(() => { if (user?.email) loadList(); }, [user, loadList]);
  useEffect(() => { loadDetail(conversationId); }, [conversationId, loadDetail]);

  const send = async () => {
    if (!conversationId || !text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/hub/conversations/${conversationId}/reply`,
        { text: text.trim() }, { headers: httpAuth() });
      setText("");
      await loadDetail(conversationId);
      await loadList();
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const draftWithAI = async () => {
    if (!conversationId) return;
    setDrafting(true);
    try {
      const r = await axios.post(`${API}/hub/conversations/${conversationId}/ai-draft`,
        {}, { headers: httpAuth() });
      setText(r.data.suggested_reply || "");
      setDetail((d) => d ? { ...d,
        conversation: { ...d.conversation,
          ai_summary: r.data.summary,
          ai_suggested_reply: r.data.suggested_reply,
        }} : d);
      toast.success("AI draft ready — edit before sending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally { setDrafting(false); }
  };

  const setStatus = async (status) => {
    if (!conversationId) return;
    try {
      await axios.post(`${API}/hub/conversations/${conversationId}/status`,
        { status }, { headers: httpAuth() });
      toast.success(`Marked ${status}`);
      await loadDetail(conversationId);
      await loadList();
    } catch { toast.error("Status update failed"); }
  };

  const setAssign = async (patch) => {
    if (!conversationId) return;
    try {
      await axios.post(`${API}/hub/conversations/${conversationId}/assign`,
        patch, { headers: httpAuth() });
      await loadDetail(conversationId);
      await loadList();
    } catch { toast.error("Assign failed"); }
  };

  const openConversation = (id) => {
    navigate(`/hub/inbox/${id}`);
  };

  const sendTextback = async () => {
    if (!conversationId) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/hub/calls/${conversationId}/textback`,
        { text: text.trim() || undefined }, { headers: httpAuth() });
      toast.success(r.data?.note || "Text-back logged");
      setText("");
      await loadDetail(conversationId);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Text-back failed");
    } finally { setBusy(false); }
  };

  const useSuggested = () => {
    if (detail?.conversation?.ai_suggested_reply) {
      setText(detail.conversation.ai_suggested_reply);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="conversation-hub-inbox">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">Conversation Hub</div>
            <h1 className="text-2xl font-bold text-slate-900">Inbox</h1>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ring-1 ring-inset inline-flex items-center gap-1 ${
                aiMode === "autonomous" ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : aiMode === "supervised" ? "bg-amber-50 text-amber-700 ring-amber-200"
                : "bg-slate-100 text-slate-600 ring-slate-200"}`}
                  data-testid="hub-ai-mode-badge">
              <Bot className="w-3 h-3" /> AI: {aiMode}
            </span>
            <span className="text-xs text-slate-500 mr-2">
              <b className="text-slate-900">{counts.unread || 0}</b> unread
            </span>
            {["", "new", "acknowledged", "won"].map((s) => (
              <button key={s || "all"}
                       onClick={() => setFilter(s)}
                       className={`text-xs px-2.5 py-1.5 rounded-full ring-1 ${
                         filter === s ? "bg-slate-900 text-white ring-slate-900"
                                      : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-100"}`}
                       data-testid={`hub-filter-${s || "all"}`}>
                {s ? s + (counts[s] ? ` · ${counts[s]}` : "") : "All"}
              </button>
            ))}
            <Link to="/hub/settings"
                   className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full ring-1 ring-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                   data-testid="hub-settings-link">
              <Settings className="w-3 h-3" /> Setup
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3 h-[calc(100vh-160px)]">
          {/* Thread list */}
          <div className="col-span-4 bg-white ring-1 ring-slate-200 rounded-xl overflow-y-auto"
                data-testid="hub-list">
            {list === null && (
              <div className="p-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
            )}
            {list?.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">
                No conversations yet. When someone fills your website form or chats with your site widget, it'll show up here.
              </div>
            )}
            {list?.map((c) => (
              <button key={c.id}
                       onClick={() => openConversation(c.id)}
                       className={`w-full text-left px-3 py-3 border-b border-slate-100 hover:bg-slate-50 ${
                         c.id === conversationId ? "bg-orange-50" : ""}`}
                       data-testid={`hub-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-slate-500 text-[11px]">
                    {CHAN_ICON[c.channel] || <Inbox className="w-3.5 h-3.5" />}
                    <span className="uppercase tracking-widest font-bold">{c.channel}</span>
                    {c.unread_owner && <span className="w-2 h-2 rounded-full bg-orange-500 ml-1" />}
                  </div>
                  <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ring-1 ring-inset ${STATUS_TONE[c.status] || STATUS_TONE.new}`}>
                    {c.status}
                  </span>
                </div>
                <div className="mt-1 font-semibold text-slate-900 text-sm truncate">
                  {c.contact?.name || "Anonymous visitor"}
                </div>
                <div className="text-xs text-slate-500 truncate">{c.subject}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                  {c.last_snippet}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {new Date(c.last_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>

          {/* Detail + reply */}
          <div className="col-span-8 bg-white ring-1 ring-slate-200 rounded-xl flex flex-col">
            {!conversationId ? (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <Inbox className="w-6 h-6 mr-2" /> Pick a conversation
              </div>
            ) : !detail ? (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1">
                        {CHAN_ICON[detail.conversation.channel]}
                        {detail.conversation.channel}
                        {detail.conversation.source_url && (
                          <a href={detail.conversation.source_url} target="_blank" rel="noreferrer"
                              className="text-sky-600 hover:underline inline-flex items-center gap-0.5 normal-case tracking-normal ml-2">
                            <ExternalLink className="w-3 h-3" /> {new URL(detail.conversation.source_url).host}
                          </a>
                        )}
                      </div>
                      <div className="font-bold text-slate-900 text-lg truncate" data-testid="hub-detail-subject">
                        {detail.conversation.subject}
                      </div>
                    </div>
                    <select value={detail.conversation.status}
                             onChange={(e) => setStatus(e.target.value)}
                             className="text-xs rounded-lg ring-1 ring-slate-200 px-2 py-1.5"
                             data-testid="hub-status-select">
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      <UserIcon className="w-3.5 h-3.5" />
                      <b className="text-slate-900">{detail.contact.name || "Anonymous"}</b>
                    </span>
                    {detail.contact.email && (
                      <a href={`mailto:${detail.contact.email}`}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-emerald-700">
                        <Mail className="w-3.5 h-3.5" /> {detail.contact.email}
                      </a>
                    )}
                    {(detail.contact.phones || [])[0] && (
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <Phone className="w-3.5 h-3.5" /> {detail.contact.phones[0]}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={detail.conversation.assigned_to || ""}
                            onChange={(e) => setDetail((d) => ({ ...d,
                              conversation: { ...d.conversation, assigned_to: e.target.value } }))}
                            onBlur={(e) => setAssign({ assigned_to: e.target.value })}
                            placeholder="Assign to (e.g. Sarah)"
                            className="text-xs rounded-lg ring-1 ring-slate-200 px-3 py-1.5"
                            data-testid="hub-assign-input" />
                    <input value={detail.conversation.next_action || ""}
                            onChange={(e) => setDetail((d) => ({ ...d,
                              conversation: { ...d.conversation, next_action: e.target.value } }))}
                            onBlur={(e) => setAssign({ next_action: e.target.value })}
                            placeholder="Next action (e.g. Call back Thu)"
                            className="text-xs rounded-lg ring-1 ring-slate-200 px-3 py-1.5"
                            data-testid="hub-next-action-input" />
                  </div>
                  {detail.conversation.ai_summary && (
                    <div className="mt-3 text-sm bg-indigo-50 text-indigo-800 ring-1 ring-indigo-100 rounded-lg px-3 py-2 italic"
                          data-testid="hub-ai-summary">
                      AI summary: {detail.conversation.ai_summary}
                    </div>
                  )}
                  {detail.conversation.ai_suggested_reply
                    && detail.conversation.last_sender === "customer"
                    && (aiMode !== "assist" || detail.conversation.missed_call) && (
                    <div className="mt-2 text-sm bg-amber-50 text-amber-900 ring-1 ring-amber-200 rounded-lg px-3 py-2 flex items-start gap-2"
                          data-testid="hub-ai-suggested-reply">
                      <Wand2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">AI suggested reply</div>
                        <div className="whitespace-pre-line">{detail.conversation.ai_suggested_reply}</div>
                      </div>
                      <button onClick={useSuggested}
                               className="text-[11px] font-bold underline shrink-0"
                               data-testid="hub-use-suggested-btn">
                        Use this
                      </button>
                    </div>
                  )}
                </div>

                {/* Timeline */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50"
                      data-testid="hub-timeline">
                  {detail.messages.map((m) => (
                    <div key={m.id}
                          className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                            m.direction === "outbound"
                              ? "ml-auto bg-slate-900 text-white rounded-br-none"
                              : "bg-white ring-1 ring-slate-200 text-slate-800 rounded-bl-none"}`}
                          data-testid={`hub-msg-${m.direction}`}>
                      <div className="whitespace-pre-line">{m.text}</div>
                      <div className="text-[10px] mt-1 opacity-70">
                        {new Date(m.at).toLocaleString()}
                        {m.sender_email ? ` · ${m.sender_email}` : ""}
                      </div>
                    </div>
                  ))}
                  {detail.related.length > 0 && (
                    <div className="pt-3 border-t border-slate-200">
                      <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-1">
                        Other conversations with {detail.contact.name || "this contact"}
                      </div>
                      {detail.related.map((r) => (
                        <Link key={r.id} to={`/hub/inbox/${r.id}`}
                               className="block text-xs text-sky-700 hover:underline">
                          → {r.subject} · <span className="text-slate-500">{r.status}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Reply pane */}
                <div className="border-t border-slate-100 p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <button onClick={draftWithAI}
                             disabled={drafting}
                             className="text-xs inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 disabled:opacity-40"
                             data-testid="hub-ai-draft-btn">
                      {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                      Draft with AI
                    </button>
                    <span className="text-[11px] text-slate-400">
                      Replying via <b className="text-slate-700 uppercase">{detail.conversation.channel}</b>
                    </span>
                  </div>
                  <textarea value={text}
                             onChange={(e) => setText(e.target.value)}
                             onKeyDown={(e) => {
                               if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                 e.preventDefault(); send();
                               }
                             }}
                             rows={4}
                             placeholder="Reply to the visitor…"
                             className="w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 focus:ring-slate-400 outline-none resize-y"
                             data-testid="hub-reply-input" />
                  <div className="mt-2 flex items-center justify-end gap-2">
                    <button onClick={() => setStatus("won")}
                             className="text-xs px-3 py-1.5 rounded-lg ring-1 ring-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                             data-testid="hub-mark-won">
                      <CheckCircle2 className="w-3 h-3" /> Mark won
                    </button>
                    <button onClick={() => setStatus("closed")}
                             className="text-xs px-3 py-1.5 rounded-lg ring-1 ring-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
                             data-testid="hub-close-btn">
                      <Archive className="w-3 h-3" /> Close
                    </button>
                    <button onClick={send}
                             disabled={busy || !text.trim()}
                             className="text-sm inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40"
                             data-testid="hub-send-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
                    </button>
                    {detail.conversation.missed_call && (
                      <button onClick={sendTextback}
                               disabled={busy}
                               className="text-sm inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
                               data-testid="hub-textback-btn"
                               title="Send an SMS text-back (currently MOCKED — no SMS provider connected)">
                        <PhoneMissed className="w-4 h-4" /> Send text-back
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
